import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { NavigationPrompt } from '../components/NavigationPrompt';
import Notify from '../utils/Notifications';

/**
 * @typedef {import('../data/WizardData').default} WizardData
 */

/**
 * Base class for wizard steps.
 * All steps must extend this class and implement the required static methods.
 * @interface
 */
export default class BaseStep extends React.Component {
  static id = null;

  static title = 'Base Step';

  /**
   * Triggers data load for this step.
   * To be overridden by subclasses if needed.
   * @param {string} search - The URL search query.
   */
  // eslint-disable-next-line no-unused-vars
  static triggerDataLoad(search) {
    // To be overridden by subclasses if needed
  }

  /**
   * Triggers search update for this step.
   * To be overridden by subclasses if needed.
   * @param {string} search - The URL search query.
   */
  // eslint-disable-next-line no-unused-vars
  static triggerSearchUpdate(search) {
    // To be overridden by subclasses if needed
  }

  /**
   * Returns the filters for this step.
   * @returns {Filters|null} The filters object or null.
   */
  // eslint-disable-next-line no-unused-vars
  static getFilters() {
    return null;
  }

  /**
   * Determines if data is loading for this step.
   * To be overridden by subclasses if needed.
   * @returns {boolean} True if data is loading, false otherwise.
   */
  // eslint-disable-next-line no-unused-vars
  static dataIsLoading() {
    return false;
  }

  /**
   * Determines if the wizard can proceed from this step.
   * To be overridden by subclasses if needed.
   * @returns {boolean} True if can proceed, false otherwise.
   */
  // eslint-disable-next-line no-unused-vars
  static canProceed() {
    return true;
  }

  /**
   * Determines if this step has a filter button in the actions menu.
   * To be overridden by subclasses if needed.
   * @returns {boolean} True if has filter button, false otherwise.
   */
  static hasFilter() {
    return false;
  }

  /**
   * Provides access to the notification system.
   * @returns {Notify} The notification instance.
   */
  static get notification() {
    return Notify;
  }

  /**
   * Private static property to hold the wizard data for this step.
   * @type {WizardData|undefined}
   */
  static #wizardData;

  /**
   * @param {WizardData} wizardData - The wizard data object to set for this step.
   */
  static set wizardData(wizardData) {
    BaseStep.#wizardData = wizardData;
  }

  /**
   * @return {WizardData} The wizard data object.
   */
  static get wizardData() {
    if (!BaseStep.#wizardData) {
      const msg =
        'WizardData is not set for this step. Ensure to set wizardData before accessing it.';

      console.error(msg);
      throw new Error(msg);
    }

    return BaseStep.#wizardData;
  }

  // Object methods BELOW -----------------------------------------------------

  /**
   * @return {WizardData} The wizard data object.
   */
  get wizardData() {
    return this.constructor.wizardData;
  }

  /**
   * Provides access to the notification system.
   * @returns {Notify} The notification instance.
   */
  get notification() {
    return this.constructor.notification;
  }

  /**
   * Gets the history object from props.
   * @returns {History} The history object for navigation.
   */
  get history() {
    const { history } = this.props;

    return history;
  }

  /**
   * Renders a navigation prompt when attempting to leave the wizard.
   * @param {boolean} when - Condition to trigger the prompt.
   * @returns {JSX.Element} The NavigationPrompt component.
   */
  renderNavigationPrompt(when) {
    return (
      <NavigationPrompt
        when={when}
        message="Are you sure you want to leave the wizard? Your changes will be lost."
      />
    );
  }

  /**
   * Redirects to the specified URL.
   * @param {string} url - The URL to redirect to.
   * @returns {JSX.Element} The Redirect component.
   */
  renderRedirect(url) {
    return <Redirect to={url} />;
  }

  /**
   * Utility method to convert a string to an integer, returning 0 if conversion fails.
   * @param {string} string - The string to convert.
   * @returns {number} The converted integer or 0 if conversion fails.
   */
  stringToInt(string) {
    const parsed = Number.parseInt(string, 10);

    return Number.isNaN(parsed) ? 0 : parsed;
  }
}

const propTypes = {
  history: PropTypes.object.isRequired,
};

BaseStep.propTypes = propTypes;
